import { FlightConfig } from "stores/wizard/config";
import { DayName } from "uitk-react-date-picker";
import { TRAVELERS_HIDDEN_INPUT_TYPE } from "src/components/shared/TravelersField";
import { FlightLegConfig } from "stores/wizard/config/staticConfigs/flight/typings";

const travelersConfig = {
  icon: "person",
  labelToken: "wizard.roomPicker.travelers.title",
  dialogTitleToken: "wizard.roomPicker.travelers.title",
  dialogCancelLabelToken: "wizard.roomPicker.closeButton.a11yLabel",
  childrenCaptionToken: "wizard.roomPicker.children.withInfants.caption",
  infantsCaptionToken: "wizard.roomPicker.infants.caption",
  youngestChildAgeTextToken: "",
  youngestInfantAgeTextToken: "wizard.roomPicker.youngestChildAge.text",
  invalidLessThanNTravelersMessageToken: "wizard.travelers.warning.invalidNumberOfTravelers.text",
  invalidUnattendedInfantOnLapMessageToken: "wizard.travelers.warning.unattendedInfantOnLap.text",
  minBookableTravelers: 1,
  maxBookableTravelers: 6,
  minChildAge: 2,
  maxChildAge: 17,
  minInfantAge: 0,
  maxInfantAge: 1,
  minAdultsPerRoom: 1,
  maxAdultsPerRoom: 6,
  minChildrenPerRoom: 0,
  maxChildrenPerRoom: 6,
  minInfantsPerRoom: 0,
  maxInfantsPerRoom: 6,
  maxRooms: 1,
  withRooms: false,
  withFlight: true,
  withInfants: true,
  hiddenInputType: TRAVELERS_HIDDEN_INPUT_TYPE.FLIGHT_SEARCH
};

const MAX_NUMBER_OF_ADDITIONAL_LEGS = 4;

const createLegConfigs = () => {
  const legArray = new Array<FlightLegConfig>(MAX_NUMBER_OF_ADDITIONAL_LEGS);

  //start additional leg count at 2; legs are 1-indexed with the default origin/dest input as leg 1
  for (let i = 0; i < MAX_NUMBER_OF_ADDITIONAL_LEGS; i++) {
    legArray[i] = flightLegConfig(i + 1);
  }

  return legArray;
};

const getMaxNumberOfAdditionalLegs = () => {
  return MAX_NUMBER_OF_ADDITIONAL_LEGS;
};

export const flightLegConfig = (legNumber: number) => {
  return {
    date: {
      ISOSubmissionSupport: false,
      format: "yyyy-MM-dd",
      submissionFormat: "yyyy-MM-dd",
      firstDayOfWeek: DayName.SUN,
      daysBookableInAdvance: 500,
      midnightBookingOffset: 4,
      maxDaysRange: 0,
      invalidStartDateMessageToken: "wizard.datePicker.warning.invalidDepartingDate.text",
      invalidEndDateMessageToken: "",
      invalidMaxDatesRangeMessageToken: "",
      invalidStartDateOutOfRangeMessageToken: "",
      invalidEndDateOutOfRangeMessageToken: "",
      start: {
        labelToken: "wizard.departing.label"
      },
      end: {
        labelToken: "wizard.returning.label"
      },
      queryParam: {
        start: "",
        end: ""
      },
      dialogId: `flights-departure-${legNumber}`
    },
    location: {
      destination: {
        inputName: `leg${legNumber}-destination`,
        icon: "place",
        invalidLocationMessageKey: "wizard.destination.warning.invalidDestination.text",
        labelToken: "wizard.flight.destination.label",
        pwaDialogQueryParam: `destination-flight-${legNumber}`,
        essAdapterConfig: {
          lob: "FLIGHTS",
          client: "Homepage",
          features: "uta_client|nearby_airport|ta_hierarchy",
          maxResults: 7,
          regionType: 95
        }
      },
      origin: {
        inputName: `leg${legNumber}-origin`,
        icon: "place",
        invalidLocationMessageKey: "wizard.origin.warning.invalidOrigin.text",
        labelToken: "wizard.origin.label",
        pwaDialogQueryParam: `origin-flight-${legNumber}`,
        essAdapterConfig: {
          lob: "FLIGHTS",
          client: "Homepage",
          features: "uta_client|nearby_airport|ta_hierarchy",
          maxResults: 7,
          regionType: 95
        }
      }
    }
  };
};

export const flightConfig: FlightConfig = {
  navigation: {
    tabIcon: "lob_flights",
    tabTitleToken: "wizard.flight.tab.title.label"
  },
  lastSearch: {
    lastSearchKey: "lastSearchCookie",
    lobKey: "wizard-flight-pwa"
  },
  location: {
    destination: {
      inputName: "leg1-destination",
      icon: "place",
      invalidLocationMessageKey: "wizard.destination.warning.invalidDestination.text",
      labelToken: "wizard.flight.destination.label",
      pwaDialogQueryParam: "destination-flight",
      essAdapterConfig: {
        lob: "FLIGHTS",
        client: "Homepage",
        features: "uta_client|nearby_airport|ta_hierarchy",
        maxResults: 7,
        regionType: 95
      }
    },
    origin: {
      inputName: "leg1-origin",
      icon: "place",
      invalidLocationMessageKey: "wizard.origin.warning.invalidOrigin.text",
      labelToken: "wizard.origin.label",
      pwaDialogQueryParam: "origin-flight",
      essAdapterConfig: {
        lob: "FLIGHTS",
        client: "Homepage",
        features: "uta_client|nearby_airport|ta_hierarchy",
        maxResults: 7,
        regionType: 95
      }
    }
  },
  date: {
    ISOSubmissionSupport: false,
    format: "yyyy-MM-dd",
    submissionFormat: "dd/MM/yyyy",
    firstDayOfWeek: DayName.SUN,
    daysBookableInAdvance: 500,
    midnightBookingOffset: 4,
    maxDaysRange: 331,
    invalidStartDateMessageToken: "wizard.datePicker.warning.invalidDepartingDate.text",
    invalidEndDateMessageToken: "wizard.datePicker.warning.invalidReturningDate.text",
    invalidMaxDatesRangeMessageToken: "wizard.datePicker.warning.maxDatesRange.text",
    invalidStartDateOutOfRangeMessageToken: "",
    invalidEndDateOutOfRangeMessageToken: "",
    start: {
      labelToken: "wizard.departing.label"
    },
    end: {
      labelToken: "wizard.returning.label"
    },
    queryParam: {
      start: "fromDate",
      end: "toDate"
    },
    dialogId: "flights-departure-arrival"
  },
  hotelDate: {
    ISOSubmissionSupport: false,
    format: "yyyy-MM-dd",
    submissionFormat: "dd/MM/yyyy",
    firstDayOfWeek: DayName.SUN,
    daysBookableInAdvance: 500,
    midnightBookingOffset: 4,
    maxDaysRange: 26,
    invalidStartDateMessageToken: "wizard.datePicker.warning.invalidFromDate.text",
    invalidEndDateMessageToken: "wizard.datePicker.warning.invalidToDate.text",
    invalidMaxDatesRangeMessageToken: "wizard.datePicker.warning.maxDatesRange.text",
    invalidStartDateOutOfRangeMessageToken: "wizard.datePicker.warning.checkInDateOutOfRange.text",
    invalidEndDateOutOfRangeMessageToken: "wizard.datePicker.warning.checkOutDateOutOfRange.text",
    start: {
      labelToken: "datePicker.fromDate.label"
    },
    end: {
      labelToken: "datePicker.toDate.label"
    },
    queryParam: {
      start: "checkInDate",
      end: "checkOutDate"
    },
    dialogId: "check-in-out"
  },
  travelers: travelersConfig,
  travelersWithHotel: {
    ...travelersConfig,
    maxRooms: 6,
    withRooms: true,
    withFlight: true,
    withInfants: true,
    hiddenInputType: TRAVELERS_HIDDEN_INPUT_TYPE.PACKAGE_SEARCH
  },
  travelersWithCar: {
    ...travelersConfig,
    hiddenInputType: TRAVELERS_HIDDEN_INPUT_TYPE.PACKAGE_SEARCH
  },
  form: {
    action: "",
    submitButton: {
      locToken: "wizard.submit.label"
    }
  },
  multiCityFlight: {
    maxNumberOfAdditionalLegs: getMaxNumberOfAdditionalLegs(),
    legs: createLegConfigs()
  },
  heading: {
    fixedTitle: false,
    mobileLocToken: "wizard.mobile.label.flight"
  },
  elementId: "wizard-flight-pwa",
  invalidOnewayHotelStartDateMessageToken:
    "wizard.datePicker.warning.invalidOneWayCheckInDate.text",
  enableAddCar: false,
  enableAddHotel: false
};
